import React, { useCallback, useContext } from 'react';
import EDGrid from '../../../../BaseComponents/GridComponents/EDGrid';
import EDAvatar from '../../../../BaseComponents/Avatar/EDAvatar';
import EDIcon from '../../../../BaseComponents/Icon/EDIcon';
import EDTypography from '../../../../BaseComponents/Typography/EDTypography';
import KodplanPicker from '../../../Kodplan/KodplanPicker';
import EDDivider from '../../../../BaseComponents/Divider/EDDivider';
import EDButton from '../../../../BaseComponents/Buttons/Button/EDButton';
import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material';
import { join, init, last } from 'ramda';
import EDTooltip from '../../../../BaseComponents/Tooltip/EDTooltip';
import { IUserMenuProps } from './UserMenu';
import { AvenyContext } from '../../../../Features/AvenyContext';
import { useQuery } from '@apollo/client';
import UserMenuQuery from './Queries/UserMenuQuery/UserMenuQuery.gql';
import {
  UserMenuQuery as IUserMenuQuery,
  UserMenuQueryVariables as IUserMenuQueryVariables,
} from './Queries/UserMenuQuery/_QueryTypes/UserMenuQuery';
import { ActiveDatabaseContext } from '../../../ActiveDatabaseContext/ActiveDatabaseContext';
import { CookieSettingsLink } from '../../../../Features/Login/Components/LoginForm/Components/CookieSettingsLink/CookieSettingsLink';
import { useCookieSettings } from '../../../CookieSettings/CookieSettingsContext';

const convertListToTextWithTooltip = (
  list: string[],
  className: string,
  id: string
) => {
  let units: string = '';
  if (list.length > 3) {
    units = `${list[0]}, ${list[1]} och ${list.length - 2} till`;
  } else if (list.length > 2) {
    units = `${list[0]}, ${list[1]} och ${list[2]}`;
  } else {
    units = join(' och ', list);
  }

  return getTooltipComp(id, list, className, units);
};

const getTooltipComp = (
  id: string,
  list: string[],
  className: string,
  orangeText: string,
  normalText?: string
) => (
  <span>
    <EDTooltip
      id={id}
      title={
        list.length === 1
          ? list[0]
          : `${join(', ', init(list))} och ${last(list) || ''}`
      }
    >
      <span className={className}>{orangeText}</span>
    </EDTooltip>
    {normalText}
  </span>
);

const getUnitsTextComp = (
  visibleUnits: string[],
  className: string,
  allUnitsVisible: boolean
) => {
  if (allUnitsVisible) {
    return getTooltipComp(
      'units-tooltip-all',
      visibleUnits,
      className,
      'alla',
      'dina enheter'
    );
  }

  return convertListToTextWithTooltip(
    visibleUnits,
    className,
    'units-tooltip-units'
  );
};

const getRolesTextComp = (
  visibleRoles: string[],
  className: string,
  allRolesVisible: boolean
) => {
  if (allRolesVisible) {
    return getTooltipComp(
      'units-tooltip-all',
      visibleRoles,
      className,
      'alla',
      'dina roller'
    );
  }

  return convertListToTextWithTooltip(
    visibleRoles,
    className,
    'units-tooltip-roles'
  );
};

const useStyles = makeStyles()((theme: Theme) => {
  return {
    root: {
      [theme.breakpoints.down('sm')]: {
        width: theme.spacing(3),
        height: theme.spacing(3),
      },
    },
    avatar: {
      margin: theme.spacing(1, 2, 1, 1),
      width: theme.spacing(7.5),
      height: theme.spacing(7.5),
    },
    profileContainer: {
      padding: theme.spacing(1, 3, 1, 1),
    },
    buttonContainer: {
      padding: theme.spacing(1),
    },
    unitsAndRolesText: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    textContainer: {
      maxWidth: theme.spacing(50),
      [theme.breakpoints.down('sm')]: {
        maxWidth: `calc(100% - ${theme.spacing(12)})`,
      },
    },
    unitsAndRolesValue: {
      color: theme.customProperties.specialText,
    },
  };
});

const UserMenuView: React.FC<
  React.PropsWithChildren<IUserMenuProps>
> = props => {
  const { onLogoutClick, handleLogout, onClose } = props;
  const { classes } = useStyles();
  const avenyContext = useContext(AvenyContext);
  const { setOpenChoiceDialog } = avenyContext;
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const [consent, setConsent] = useCookieSettings();

  const handleLogoutClick = useCallback(() => {
    const logout = () => {
      if (onLogoutClick) {
        onLogoutClick();
      }
      handleLogout();
    };

    if (props.formHasChanged) {
      setOpenChoiceDialog(true, () => {
        logout();
      });
      onClose();
      return;
    }
    logout();
  }, [
    props.formHasChanged,
    onLogoutClick,
    handleLogout,
    onClose,
    setOpenChoiceDialog,
  ]);

  const queryResult = useQuery<IUserMenuQuery, IUserMenuQueryVariables>(
    UserMenuQuery,
    {
      variables: { dbName: activeDatabaseContext.activeDatabase ?? '' },
      skip: activeDatabaseContext.activeDatabase == null,
    }
  );
  const roles =
    queryResult.data?.me?.at(0)?.roles?.map(role => role.name) ?? [];
  return (
    <>
      <EDGrid
        container
        className={classes.profileContainer}
        spacing={0}
        wrap="nowrap"
      >
        <EDGrid item>
          <EDAvatar className={classes.avatar}>
            <EDIcon color="inherit" iconType="person" />
          </EDAvatar>
        </EDGrid>
        <EDGrid item className={classes.textContainer}>
          <EDTypography variant="subtitle1">{props.name}</EDTypography>
          <EDTypography variant="caption">{props.sign}</EDTypography>
          &nbsp;
          <EDTypography variant="caption" className={classes.unitsAndRolesText}>
            visar{' '}
            {getUnitsTextComp(
              props.visibleUnits,
              classes.unitsAndRolesValue,
              props.allUnitsVisible
            )}{' '}
            som{' '}
            {getRolesTextComp(
              roles,
              classes.unitsAndRolesValue,
              props.allRolesVisible
            )}
          </EDTypography>
          {!props.singleRoutineEnabled && <KodplanPicker />}
          <CookieSettingsLink consent={consent} setConsent={setConsent} />
        </EDGrid>
      </EDGrid>
      {props.showLogoutButton && (
        <>
          <EDDivider />
          <EDGrid
            container
            className={classes.buttonContainer}
            justifyContent="flex-end"
            spacing={0}
          >
            <EDGrid item>
              <EDButton
                label="Logga ut"
                onClick={handleLogoutClick}
                primary
                testId="Button-logOut"
              />
            </EDGrid>
          </EDGrid>
        </>
      )}
    </>
  );
};

export default UserMenuView;
